/**
 * @file RichTextWrapper.tsx
 * @description Rich text renderer with automatic external link handling, table enhancements,
 * in-place modal triggers for <div data-modal="true"> blocks, and generic tooltip modals
 * in-place modal triggers for <div data-modal="true"> blocks, and generic tooltip modals
 * driven by Bootstrap-style triggers (<a data-toggle="modal" data-target="#id">).
 */

'use client';

import React, { JSX, useEffect, useMemo, useRef, useState } from 'react';

import { CSLPFieldMapping } from '@/.generated';
import { getCSLPAttributes } from '@/utils/type-guards';
import { cn } from 'tailwind-variants';
import ModalWrapper from '@/helpers/Wrappers/ModalWrapper/ModalWrapper';
import { ScrollableTableWrapper } from '@/helpers/Wrappers/ScrollableTableWrapper/ScrollableTableWrapper';
import { getFileType, iconVariants } from '@/components/primitives/FileIcon';
import { useRedirectBaseUrl } from '@/web-component/redirect-base-url-context';
import { trackAdobeClick, trackAdobeDownload, getDocumentNameFromUrl } from '@/utils/adobe';
import { decodeHtmlEntities, escapeHtml } from '@/utils/string-utils';
import { withDeliveryParams } from '@/utils/image-utils';

interface RichTextWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Rich text HTML content from CMS */
  content?: string;
  /** Contentstack Live Preview field mapping */
  cslpAttribute?: CSLPFieldMapping;
  /** Parent class name for custom RTE styling */
  parentClassName?: string;
  /** Default title used when a data-modal div has no data-modal-title attribute */
  modalTitle?: string;
}

type TriggerTag = 'div' | 'button';

type Segment = { type: 'html'; content: string } | { type: 'scrollable-table'; content: string };

type InlineModal = { content: string; title: string };

const RichTextWrapper = ({
  content,
  className,
  cslpAttribute,
  parentClassName = 'rte',
  modalTitle,
  ...props
}: RichTextWrapperProps): JSX.Element => {
  // Web-component-only: prefix root-relative anchors inside the RTE with the external base URL
  const redirectBaseUrl = useRedirectBaseUrl();
  // Container ref drives the delegated open-handling for both tooltip and data-modal triggers
  const containerRef = useRef<HTMLDivElement>(null);
  // Holds the id of the currently open tooltip modal (null when closed)
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  // Holds the id of the currently open data-modal block (null when closed)
  const [activeInlineModal, setActiveInlineModal] = useState<string | null>(null);

  // Process links, pull out Bootstrap-style modal blocks, then mark up data-modal triggers in place
  const { html, tooltipModals, inlineModals } = useMemo(() => {
    const tooltip = extractTooltipModals(processRichTextContent(content, redirectBaseUrl));
    const inline = extractInlineModals(tooltip.html, modalTitle);
    return {
      html: inline.html,
      tooltipModals: tooltip.tooltipModals,
      inlineModals: inline.inlineModals,
    };
  }, [content, redirectBaseUrl, modalTitle]);

  // Delegate trigger activation so it works no matter how deeply nested the trigger is (e.g. table cells)
  useEffect(() => {
    const root = containerRef.current;
    if (!root || tooltipModals.size === 0) return;

    // Resolve an event target to a known tooltip id, or null when it is not a valid trigger
    const resolveTooltipId = (target: EventTarget | null): string | null => {
      const trigger = (target as HTMLElement | null)?.closest?.(
        '[data-toggle="modal"][data-target]'
      );
      if (!trigger || !root.contains(trigger)) return null;
      const id = (trigger.getAttribute('data-target') ?? '').replace(/^#/, '');
      return tooltipModals.has(id) ? id : null;
    };

    // Pointer activation (also covers Enter, which fires a native click on anchors)
    const handleClick = (e: MouseEvent) => {
      const id = resolveTooltipId(e.target);
      if (!id) return;
      e.preventDefault();
      setActiveTooltip(id);
    };

    // Space does not click an anchor by default, so honour it explicitly for role="button" triggers
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== ' ' && e.key !== 'Spacebar') return;
      const id = resolveTooltipId(e.target);
      if (!id) return;
      e.preventDefault();
      setActiveTooltip(id);
    };

    root.addEventListener('click', handleClick);
    root.addEventListener('keydown', handleKeyDown);
    return () => {
      root.removeEventListener('click', handleClick);
      root.removeEventListener('keydown', handleKeyDown);
    };
  }, [tooltipModals]);

  // Delegate data-modal activation too, so the trigger can stay exactly where the author nested it
  useEffect(() => {
    const root = containerRef.current;
    if (!root || inlineModals.size === 0) return;

    // Resolve an event target to the nearest known data-modal trigger element, or null
    const resolveTrigger = (target: EventTarget | null): HTMLElement | null => {
      const trigger = (target as HTMLElement | null)?.closest?.(
        '[data-rte-modal]'
      ) as HTMLElement | null;
      if (!trigger || !root.contains(trigger)) return null;
      return inlineModals.has(trigger.dataset.rteModal ?? '') ? trigger : null;
    };

    // Pointer activation, plus the native click a real <button> fires for Enter/Space
    const handleClick = (e: MouseEvent) => {
      const trigger = resolveTrigger(e.target);
      if (!trigger) return;
      setActiveInlineModal(trigger.dataset.rteModal ?? null);
    };

    // role="button" divs get no native click from the keyboard, so honour Enter/Space explicitly
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      const trigger = resolveTrigger(e.target);
      // Skip real buttons — preventing default here would cancel their own click
      if (!trigger || trigger.tagName === 'BUTTON') return;
      e.preventDefault();
      setActiveInlineModal(trigger.dataset.rteModal ?? null);
    };

    root.addEventListener('click', handleClick);
    root.addEventListener('keydown', handleKeyDown);
    return () => {
      root.removeEventListener('click', handleClick);
      root.removeEventListener('keydown', handleKeyDown);
    };
  }, [inlineModals]);

  // Delegate Adobe tracking so every link/button rendered inside the RTE fires a Click event;
  // links pointing to a document (pdf/doc/xls/ppt…) additionally fire a Download event.
  // Re-runs when content changes so the listener is attached once the markup exists.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    // Track the nearest link/button ancestor of the click; both trackers derive type/location/url from it
    const handleTrackClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement | null)?.closest('a, button, [role="button"]');
      if (!el || !root.contains(el)) return;
      const clickName = getRteClickName(el);
      const documentHref = getDocumentHref(el);
      if (documentHref) {
        // trackAdobeDownload fires the Click event first, then the Download event
        trackAdobeDownload(
          { clickName, documentName: getDocumentNameFromUrl(documentHref), clickType: 'file-download' },
          el
        );
        return;
      }
      trackAdobeClick({ clickName }, el);
    };

    root.addEventListener('click', handleTrackClick);
    return () => root.removeEventListener('click', handleTrackClick);
  }, [html]);

  if (!html) return <></>;

  // data-modal blocks stay inline in the markup, so only flagged tables need slicing out
  const segments = splitScrollableTables(html);
  const activeContent = activeTooltip ? tooltipModals.get(activeTooltip) : undefined;
  const activeInline = activeInlineModal ? inlineModals.get(activeInlineModal) : undefined;

  // One shared modal serves every tooltip trigger in this RTE instance
  const tooltipModal = activeContent ? (
    <ModalWrapper
      isOpen
      onClose={() => setActiveTooltip(null)}
      title={modalTitle ?? 'More information'}
      size="md"
    >
      <div className="rte" dangerouslySetInnerHTML={{ __html: activeContent }} />
    </ModalWrapper>
  ) : null;

  // One shared modal serves every data-modal trigger in this RTE instance
  const inlineModal = activeInline ? (
    <ModalWrapper
      isOpen
      onClose={() => setActiveInlineModal(null)}
      title={activeInline.title}
      size="xl"
    >
      <h3 className="heading-3 mb-4">{activeInline.title}</h3>
      <div className="rte" dangerouslySetInnerHTML={{ __html: activeInline.content }} />
    </ModalWrapper>
  ) : null;

  // Fast path: no scrollable tables – preserve the original single-div render
  if (segments.length === 1 && segments[0].type === 'html') {
    return (
      <>
        <div
          {...props}
          ref={containerRef}
          className={cn(parentClassName, className)}
          dangerouslySetInnerHTML={{ __html: segments[0].content }}
          {...getCSLPAttributes(cslpAttribute)}
        />
        {tooltipModal}
        {inlineModal}
      </>
    );
  }

  return (
    <>
      <div
        {...props}
        ref={containerRef}
        className={cn(parentClassName, className)}
        {...getCSLPAttributes(cslpAttribute)}
      >
        {segments.map((seg, i) =>
          seg.type === 'html' ? (
            <div key={i} dangerouslySetInnerHTML={{ __html: seg.content }} />
          ) : (
            <ScrollableTableWrapper key={i}>
              <div dangerouslySetInnerHTML={{ __html: seg.content }} />
            </ScrollableTableWrapper>
          )
        )}
      </div>
      {tooltipModal}
      {inlineModal}
    </>
  );
};

/**
 * Read an attribute value out of a tag's attribute-blob string (no DOM).
 */
function getAttr(attrs: string, name: string): string | undefined {
  const re = new RegExp(`\\b${name}=["']([^"']*)["']`, 'i');
  return attrs.match(re)?.[1];
}

// Build a readable Click event label from a rendered RTE element, ignoring sr-only helper text
function getRteClickName(el: Element): string {
  const aria = el.getAttribute('aria-label')?.trim();
  if (aria) return aria;
  // Clone so removing the sr-only nodes (e.g. "(Opens in a new tab)") never mutates the live DOM
  const clone = el.cloneNode(true) as Element;
  clone.querySelectorAll('.sr-only').forEach((node) => node.remove());
  const text = clone.textContent?.replace(/\s+/g, ' ').trim();
  return (
    text ||
    el.getAttribute('title')?.trim() ||
    (el.tagName.toLowerCase() === 'a' ? 'Link' : 'Button')
  );
}

// Returns the absolute href when the element is an anchor pointing to a known document type, else null
function getDocumentHref(el: Element): string | null {
  if (el.tagName.toLowerCase() !== 'a') return null;
  const href = (el as HTMLAnchorElement).href;
  if (!href) return null;
  try {
    // The href property is always absolute; use its pathname so query/hash never affect the extension
    return getFileType(new URL(href).pathname) ? href : null;
  } catch {
    return null;
  }
}

/**
 * Turn every <div data-modal="true">…</div> / <button data-modal="true">…</button>
 * into an accessible trigger *in place* and capture its inner HTML for the modal.
 *
 * The block is rewritten rather than sliced out because slicing put an ancestor's
 * opening and closing tags into different dangerouslySetInnerHTML segments — the
 * parser then auto-closed the orphan open tag and dropped the orphan close tag,
 * collapsing the surrounding element. Matching is depth-aware, so nested same-tag
 * elements inside a block are handled correctly.
 */
function extractInlineModals(
  html: string,
  defaultTitle?: string
): { html: string; inlineModals: Map<string, InlineModal> } {
  const inlineModals = new Map<string, InlineModal>();
  if (!html) return { html: '', inlineModals };

  const openRe = /<(div|button)\b([^>]*\bdata-modal=["']true["'][^>]*)>/gi;
  let result = '';
  let lastIndex = 0;
  let open: RegExpExecArray | null;

  while ((open = openRe.exec(html)) !== null) {
    const tag = open[1].toLowerCase() as TriggerTag;
    const attrs = open[2];
    const contentStart = open.index + open[0].length;
    const closeIndex = findMatchingClose(html, contentStart, tag);
    if (closeIndex === -1) break;

    const inner = html.slice(contentStart, closeIndex);
    const id = `rte-modal-${inlineModals.size}`;
    inlineModals.set(id, {
      content: inner,
      title: getAttr(attrs, 'data-modal-title') ?? defaultTitle ?? 'Details',
    });

    // data-trigger-content shows that text on the trigger instead of the modal body;
    // decode-then-escape keeps authored entities intact while blocking injected markup
    const triggerContent = getAttr(attrs, 'data-trigger-content');
    const triggerInner = triggerContent
      ? escapeHtml(decodeHtmlEntities(triggerContent))
      : inner;

    result += `${html.slice(lastIndex, open.index)}<${tag}${buildTriggerAttrs(tag, attrs, id)}>${triggerInner}</${tag}>`;

    lastIndex = closeIndex + `</${tag}>`.length;
    // Skip past the whole block so a nested data-modal never becomes a second trigger
    openRe.lastIndex = lastIndex;
  }

  if (inlineModals.size === 0) return { html, inlineModals };
  return { html: result + html.slice(lastIndex), inlineModals };
}

/** Extend an authored trigger's attributes with the modal marker and button semantics it lacks. */
function buildTriggerAttrs(tag: TriggerTag, attrs: string, id: string): string {
  const parts = [attrs.trim(), `data-rte-modal="${id}"`];
  if (!/\baria-haspopup=/i.test(attrs)) parts.push('aria-haspopup="dialog"');
  if (tag === 'button') {
    if (!/\btype=/i.test(attrs)) parts.push('type="button"');
  } else {
    if (!/\brole=/i.test(attrs)) parts.push('role="button"');
    if (!/\btabindex=/i.test(attrs)) parts.push('tabindex="0"');
  }
  // Prepend cursor-pointer to the authored class list, or add the attribute when there is none
  const joined = parts.join(' ');
  return /\bclass=["']/i.test(attrs)
    ? ` ${joined.replace(/\bclass=(["'])/i, 'class=$1cursor-pointer ')}`
    : ` ${joined} class="cursor-pointer"`;
}

/**
 * Slice HTML into html/scrollable-table segments at each
 * <table … data-scrollable-table="true">…</table>. Depth-aware matching handles
 * nested tables so the wrapper closes on the correct </table>.
 */
function splitScrollableTables(html: string): Segment[] {
  const segments: Segment[] = [];
  const openRe = /<table\b[^>]*\bdata-scrollable-table=["']true["'][^>]*>/gi;
  let lastIndex = 0;
  let open: RegExpExecArray | null;

  while ((open = openRe.exec(html)) !== null) {
    const contentStart = open.index + open[0].length;
    const closeIndex = findMatchingClose(html, contentStart, 'table');
    if (closeIndex === -1) break;
    const end = closeIndex + '</table>'.length;

    const before = html.slice(lastIndex, open.index);
    if (before.trim()) segments.push({ type: 'html', content: before });
    segments.push({ type: 'scrollable-table', content: html.slice(open.index, end) });

    lastIndex = end;
    openRe.lastIndex = end;
  }

  const tail = html.slice(lastIndex);
  if (tail.trim()) segments.push({ type: 'html', content: tail });

  // Guarantee a single html segment when nothing was extracted so the fast path holds
  if (segments.length === 0) segments.push({ type: 'html', content: html });

  return segments;
}

/** Escape a string for safe use inside a dynamically built RegExp */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Find the index of the closing tag that matches an already-opened element,
 * honouring nesting of same-name tags. Returns -1 when no match is found.
 */
function findMatchingClose(html: string, fromIndex: number, tag: string): number {
  const re = new RegExp(`<(/?)${tag}\\b[^>]*>`, 'gi');
  re.lastIndex = fromIndex;
  let depth = 1;
  let match: RegExpExecArray | null;

  while ((match = re.exec(html)) !== null) {
    // Self-closing tags neither open nor close a scope
    if (/\/>\s*$/.test(match[0])) continue;
    if (match[1] === '/') {
      depth -= 1;
      if (depth === 0) return match.index;
    } else {
      depth += 1;
    }
  }
  return -1;
}

/**
 * Locate an element by its id attribute and return the slice bounds plus inner HTML.
 * Uses depth-aware matching so nested same-name tags are handled correctly.
 */
function extractElementById(
  html: string,
  id: string
): { start: number; end: number; inner: string } | null {
  const openRe = new RegExp(
    `<([a-z][a-z0-9]*)\\b[^>]*\\bid=["']${escapeRegExp(id)}["'][^>]*>`,
    'i'
  );
  const open = openRe.exec(html);
  if (!open) return null;

  const tag = open[1];
  const contentStart = open.index + open[0].length;
  const closeIndex = findMatchingClose(html, contentStart, tag);
  if (closeIndex === -1) return null;

  return {
    start: open.index,
    end: closeIndex + `</${tag}>`.length,
    inner: html.slice(contentStart, closeIndex),
  };
}

/** Return the inner HTML of the first .modal-body element, or null when absent */
function extractModalBody(html: string): string | null {
  const openRe = /<([a-z][a-z0-9]*)\b[^>]*\bclass=["'][^"']*\bmodal-body\b[^"']*["'][^>]*>/i;
  const open = openRe.exec(html);
  if (!open) return null;

  const tag = open[1];
  const contentStart = open.index + open[0].length;
  const closeIndex = findMatchingClose(html, contentStart, tag);
  if (closeIndex === -1) return null;

  return html.slice(contentStart, closeIndex);
}

/**
 * Pull Bootstrap-style modal blocks out of the RTE markup so they never render inline.
 *
 * For every trigger (`<… data-toggle="modal" data-target="#id">`) the element with the
 * referenced id is located, its `.modal-body` captured, and the whole block stripped from
 * the HTML. The returned map is keyed by id and consumed by the delegated click handler.
 */
function extractTooltipModals(html: string): { html: string; tooltipModals: Map<string, string> } {
  const tooltipModals = new Map<string, string>();
  if (!html) return { html: '', tooltipModals };

  // Collect every modal id referenced by a trigger element
  const ids = new Set<string>();
  const triggerRe = /<[a-z][a-z0-9]*\b[^>]*\bdata-toggle=["']modal["'][^>]*>/gi;
  let trigger: RegExpExecArray | null;
  while ((trigger = triggerRe.exec(html)) !== null) {
    const target = getAttr(trigger[0], 'data-target');
    if (target?.startsWith('#')) ids.add(target.slice(1));
  }

  if (ids.size === 0) return { html, tooltipModals };

  // Capture each referenced block, then remove it from the working copy
  let result = html;
  ids.forEach((id) => {
    const block = extractElementById(result, id);
    if (!block) return;
    tooltipModals.set(id, (extractModalBody(block.inner) ?? block.inner).trim());
    result = result.slice(0, block.start) + result.slice(block.end);
  });

  return { html: result, tooltipModals };
}

/**
 * True when a link's inner HTML already contains an icon, so no icon is injected on top of it.
 * Heuristic tuned to this codebase: Font Awesome on <i>/<span>, or an inline <svg>. Bare <i> counts
 * because the RTE emits <em> for italics, so <i> here is only ever an icon.
 */
function hasAuthoredIcon(innerHtml: string): boolean {
  if (/<(?:i|svg)\b/i.test(innerHtml)) return true;
  return /<[a-z][a-z0-9]*\b[^>]*\bclass=["'][^"']*\bfa-/i.test(innerHtml);
}

/** True when a link already carries visually hidden text, so the new-tab warning is not repeated. */
function hasScreenReaderText(innerHtml: string): boolean {
  return /\bclass=["'][^"']*\bsr-only\b/i.test(innerHtml);
}

/**
 * Processes rich text HTML content without DOM APIs — server and client compatible.
 *
 * Processing steps:
 * 1. Prefixes root-relative anchor hrefs with redirectBaseUrl when supplied (web-component integration).
 * 2. Appends new tab icon and screen reader text inside links with target="_blank"
 * 3. Adds a default title to authored iframes that are missing one (WCAG 2.4.1)
 * 4. Appends a file-type icon inside links whose href points to a known document type
 *    (pdf/doc/docx/xls/xlsx/ppt/pptx). Image-wrapping links (`<a><img/></a>`) are skipped.
 *
 * Steps 2 and 4 never duplicate what the author already wrote: a link that already contains an
 * icon keeps only that icon, making these passes idempotent over already-processed markup.
 */
function processRichTextContent(content?: string, redirectBaseUrl?: string): string {
  if (!content) return '';

  // Prefix root-relative hrefs (e.g. /privacy-policy) with the external base URL when provided.
  // Protocol-relative (//), absolute (https://), hash, mailto:, tel: are skipped.
  let processed = content;
  if (redirectBaseUrl) {
    const trimmedBase = redirectBaseUrl.endsWith('/')
      ? redirectBaseUrl.slice(0, -1)
      : redirectBaseUrl;
    processed = processed.replace(
      /(<a\s[^>]*?href=)(["'])(\/(?!\/)[^"']*)\2/gi,
      (_match, prefix, quote, path) => `${prefix}${quote}${trimmedBase}${path}${quote}`
    );
  }

  // Append sr-only text and icon inside target="_blank" links — but skip mailto/tel links,
  // image-wrapping links, and document links (which receive a file-type icon in the pass below).
  processed = processed.replace(
    /(<a\s[^>]*target=["']_blank["'][^>]*>)([\s\S]*?)(<\/a>)/gi,
    (match, openTag, innerHtml, closeTag) => {
      const href = getAttr(openTag, 'href') ?? '';
      const pathname = href.split('?')[0].split('#')[0];
      // mailto:/tel: are not external page navigations; images are their own visual; documents get a file icon
      const isMailOrTel = /^\s*(mailto:|tel:)/i.test(href);
      const wrapsImage = /<img\b/i.test(innerHtml);
      if (isMailOrTel || wrapsImage || getFileType(pathname)) return match;
      // Add only what the author left out — an authored icon suppresses ours, authored sr-only text suppresses the warning
      const srOnlyText = hasScreenReaderText(innerHtml)
        ? ''
        : '<span class="sr-only"> (Opens in a new tab)</span>';
      const icon = hasAuthoredIcon(innerHtml)
        ? ''
        : '<i class="fa-solid fa-arrow-up-right text-secondary ms-1" aria-hidden="true"></i>';
      if (!srOnlyText && !icon) return match;
      return `${openTag}${innerHtml}${srOnlyText}${icon}${closeTag}`;
    }
  );

  // Give authored iframes missing a title a default so screen readers don't voice the frame URL (WCAG 2.4.1)
  processed = processed.replace(/<iframe\b(?![^>]*\btitle=)/gi, '<iframe title="Embedded content"');

  // Append file-type icon inside links pointing to recognised document extensions
  processed = processed.replace(
    /(<a\s[^>]*?href=["'])([^"']+)(["'][^>]*>)([\s\S]*?)(<\/a>)/gi,
    (match, preHref, href, postHref, innerHtml, closeTag) => {
      // Skip image-wrapping links — the image itself is the visual, no file icon needed
      if (/<img\b/i.test(innerHtml)) return match;
      // Skip links the author already gave an icon — theirs wins, ours would be a duplicate
      if (hasAuthoredIcon(innerHtml)) return match;
      // Strip query string and hash before resolving the extension
      const pathname = href.split('?')[0].split('#')[0];
      const fileType = getFileType(pathname);
      if (!fileType) return match;
      return `${preHref}${href}${postHref}${innerHtml}<i class="${iconVariants({ fileType })} ms-1" aria-hidden="true"></i>${closeTag}`;
    }
  );

  // Let Contentstack serve WebP for authored images — decode entities before parsing so an escaped
  // query string (&amp;) doesn't turn into a bogus param, then re-escape for the attribute
  processed = processed.replace(
    /(<img\s[^>]*?src=)(["'])([^"']+)\2/gi,
    (match, prefix: string, quote: string, src: string) => {
      const decoded = decodeHtmlEntities(src);
      // No width/quality: authored images keep their natural size, we only switch the format to WebP
      const optimised = withDeliveryParams(decoded, {});
      if (!optimised || optimised === decoded) return match;
      return `${prefix}${quote}${escapeHtml(optimised)}${quote}`;
    }
  );

  return processed;
}

export default RichTextWrapper;
